import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { decorate, injectable, inject, LazyServiceIdentifier } from 'inversify'

// evertel
import { Api } from '@evertel/api'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { DeviceTokenService } from './DeviceTokenService'
import { SessionService } from './SessionService'
import { CurrentUserPollService, CurrentUserController } from '@evertel/blue-user'
import { getPrimaryDepartmentsAccess } from '@evertel/utils'
import { APIDataBlueUser } from '@evertel/types'

// Result types

// GCWSCSR-915: returned by the enterprise auto-join endpoint (202) when the
// agency has no seats available — the user is logged in but NOT added to the agency
export type EnterpriseJoinNotice = {
    joined: false
    code: 'NOT_ENOUGH_SEATS'
    message: string
    departmentId?: number
    departmentName?: string
}

type LoginResult = {
    success: true
    user: APIDataBlueUser
    enterpriseJoinNotice?: EnterpriseJoinNotice
}

type EnterpriseResult = {
    success: true
    redirectUrl: string
}

type PasswordLoginResult =
    | LoginResult
    | EnterpriseResult
    | { success: true, mfaRequired: true }

type MFAResult = LoginResult

type PasswordResetRequestResult = {
    success: true
    message: string
}

type PasswordResetResult = {
    success: true
}

// Token marker of the API's Device row for an E2E bypass session.
const E2E_BYPASS_DEVICE_TOKEN = 'e2e-bypass'

class LoginService {
    // Internal 2FA state
    private mfaToken?: string
    private oobCode?: string

    //Credentials for auto login
    private tempEmail?: string
    private tempPassword: string

    public loggedEmail?: string
    public startupActionsDisabled = false

    onLoginCallback?: () => void = undefined

    constructor(
        private authenticationController: AuthenticationController,
        private deviceTokenService: DeviceTokenService,
        private sessionService: SessionService,
        private currentUserPollService: CurrentUserPollService,
        private currentUserController: CurrentUserController,
        private api: Api
    ) {
        makeAutoObservable(this)

        // Every way a session ends (logout, token expiry, e2e/clear) sets
        // currentUserId to 0, and this singleton outlives the session
        reaction(
            () => this.sessionService.currentUserId,
            (userId, previousUserId) => {
                if (!userId && previousUserId) this.resetSessionState()
            }
        )
    }

    /**
     * Clear the state that belongs to one session. onLoginCallback and the
     * temporary credentials are kept: they carry a deep link and a registration
     * across the logout into the next login.
     */
    resetSessionState(): void {
        this.startupActionsDisabled = false
        this.mfaToken = undefined
        this.oobCode = undefined
    }

    /**
     * Set startup actions disabled state
     */
    setStartupActionsDisabled(disabled: boolean): void {
        this.startupActionsDisabled = disabled
    }

    /**
     * Check if email belongs to an enterprise (SSO) account
     * Throws 404 error if not enterprise
     */
    async checkEnterprise(email: string, redirectUri?: string): Promise<EnterpriseResult> {
        const redirectUrl = await this.authenticationController.enterpriseLogin(email, redirectUri)
        return { success: true, redirectUrl }
    }

    /**
     * Login with email and password
     * Internally calls authenticateUser if access token is received
     */
    async loginWithPassword(email: string, password: string): Promise<PasswordLoginResult> {
        const body = {
            email,
            password,
            deviceToken: this.deviceTokenService.token
        }

        this.loggedEmail = email

        const response = await this.authenticationController.loginToAuth0(body)

        // Check for SSO redirect
        if (typeof response === 'string') {
            if (response.includes('https')) {
                return { success: true, redirectUrl: response }
            } else {
                throw new Error('Invalid login response')
            }
        }

        // Check for 2FA requirement
        if (response.mfaToken) {
            runInAction(() => {
                this.mfaToken = response.mfaToken
                this.oobCode = response.oobCode
            })
            return { success: true, mfaRequired: true }
        }

        // Normal login - authenticate with access token
        return await this.authenticateUser(response.access_token)
    }

    /**
     * Complete authentication with access token
     * Called internally or from SSO callback
     */
    async authenticateUser(accessToken: string): Promise<LoginResult> {
        const bodyAPIRequest = {
            auth0Token: accessToken,
            deviceToken: this.deviceTokenService.token
        }

        const loginResponse = await this.authenticationController.postLogin(bodyAPIRequest)

        // Extract response fields
        const { id: authToken, userId, deviceId, user } = loginResponse

        // Set session data, must be grouped
        runInAction(() => {
            this.sessionService.setDeviceId(deviceId)
            this.sessionService.setAuthToken(authToken)
            // set last just in case (runInAction should make it irrelevant)
            this.sessionService.setCurrentUserId(userId)
        })

        // Resolve primary department - using same logic as original stores

        // Get current user details
        await this.currentUserPollService.fetchUserDetails()

        // TODO: user.departmentsAccess doesn't exist and currentUserPollService causes infinite loop???
        const primaryDepartmentId = getPrimaryDepartmentsAccess(this.currentUserController.departmentsAccess)?.departmentId

        if (primaryDepartmentId) {
            this.sessionService.setSelectedDeparmentId(primaryDepartmentId)
        }

        // Handle enterprise SSO auto-join if user has no department
        let enterpriseJoinNotice: EnterpriseJoinNotice | undefined
        if (!primaryDepartmentId && user.auth0?.enterprise) {
            try {
                const joinResult = await this.authenticationController.joinEnterpriseAccount(user.id) as any
                // await this.currentUserPollService.fetchUserDetails()

                // GCWSCSR-915: agency full — user logged in but not added to the
                // agency. Surface the notice so the login screens can message the user.
                if (joinResult?.joined === false && joinResult?.code === 'NOT_ENOUGH_SEATS') {
                    enterpriseJoinNotice = joinResult as EnterpriseJoinNotice
                }
            } catch (error: any) {
                if (error.code === 'AUTO_JOIN_FAILED') {
                    // Let component handle this navigation
                    throw error
                }
                throw error
            }
        }

        return {
            success: true,
            user: loginResponse,
            enterpriseJoinNotice
        }
    }

    /**
     * E2E authentication bypass for testing
     * Authenticates user directly with userId without Auth0 validation
     * Only used when the E2E_AUTH launch flag is enabled
     */
    async authenticateUserE2E(userId: string): Promise<void> {
        const tryAsUserId = parseInt(userId, 10)
        if (isNaN(tryAsUserId) && tryAsUserId >= 100000) return

        // Set session data, must be grouped
        runInAction(() => {
            this.sessionService.setAuthToken(userId)
            // set last just in case (runInAction should make it irrelevant)
            this.sessionService.setCurrentUserId(tryAsUserId)
        })

        // The API backs a numeric-token session with one Device row per user,
        // marked by its token (apps/evertel-api/src/server/middleware/access-token.js)
        // and created before this request reaches its handler. Per-device
        // settings (notification switches, sounds) are read and written on it.
        const [bypassDevice] = await this.api.Routes.BlueUser.getDevices(tryAsUserId, { where: { token: E2E_BYPASS_DEVICE_TOKEN } })
        this.sessionService.setDeviceId(bypassDevice?.id ?? 0)

        // Resolve primary department - using same logic as original stores

        // Get current user details
        await this.currentUserPollService.fetchUserDetails()

        // TODO: user.departmentsAccess doesn't exist and currentUserPollService causes infinite loop???
        const primaryDepartmentId = getPrimaryDepartmentsAccess(this.currentUserController.departmentsAccess)?.departmentId

        if (primaryDepartmentId) {
            this.sessionService.setSelectedDeparmentId(primaryDepartmentId)
        } else {
            // Handle enterprise SSO auto-join if user has no department
            try {
                await this.authenticationController.joinEnterpriseAccount(tryAsUserId)
                // await this.currentUserPollService.fetchUserDetails()
            } catch (error: any) {
                if (error.code === 'AUTO_JOIN_FAILED') {
                    // Let component handle this navigation
                    throw error
                }
                throw error
            }
        }
    }

    /**
     * Set callback after log in
     */
    setOnLoginCallback(onLoginCb?: () => void) {
        this.onLoginCallback = onLoginCb
    }

    /**
     * Challenge user for 2FA (initiate 2FA process)
     */
    async challengeMFA(options: any): Promise<{ success: true }> {
        if (!this.mfaToken) {
            throw new Error('No 2FA token available. Please login first.')
        }

        const response = await this.authenticationController.challengeUser(this.mfaToken, options)

        runInAction(() => {
            this.oobCode = response.oob_code
        })

        return { success: true }
    }

    /**
     * Verify 2FA code
     * Internally calls authenticateUser if successful
     */
    async verifyMFA(code: string): Promise<MFAResult> {
        if (!this.mfaToken) {
            throw new Error('No 2FA token available. Please initiate 2FA challenge first.')
        }

        const bodyParams = {
            mfaToken: this.mfaToken,
            oobCode: this.oobCode,
            bindingCode: code,
            deviceToken: this.deviceTokenService.token
        }

        const response = await this.authenticationController.verifyChallenge(bodyParams)

        // Handle verification errors
        if (response.error) {
            throw new Error(response.error_description)
        }

        // Clear 2FA state
        runInAction(() => {
            this.mfaToken = undefined
            this.oobCode = undefined
        })

        // Complete authentication with access token
        return await this.authenticateUser(response.access_token)
    }

    /**
     * Request password reset
     */
    async requestPasswordReset(email: string): Promise<PasswordResetRequestResult> {
        const response = await this.authenticationController.forgotPassword(email)
        return {
            success: true,
            message: response?.message || 'Password reset email sent'
        }
    }

    /**
     * Reset password with token
     */
    async resetPassword(newPassword: string, confirmPassword: string, token: string): Promise<PasswordResetResult> {
        const body = {
            password: newPassword,
            confirmation: confirmPassword
        }

        await this.authenticationController.resetForgotPassword(body, token)

        return { success: true }
    }

    /**
     * Store temporary credentials for auto login flows
     */
    setTempCredentials(email: string, password: string): void {
        runInAction(() => {
            this.tempEmail = email
            this.tempPassword = password
        })
    }

    /**
     * Get temporary credentials for auto login flows
     */
    getTempCredentials(): { email?: string, password?: string } {
        return {
            email: this.tempEmail,
            password: this.tempPassword
        }
    }

    /**
     * Clear temporary credentials
     */
    clearTempCredentials(): void {
        runInAction(() => {
            this.tempEmail = undefined
            this.tempPassword = undefined
        })
    }

    /**
     * Clear any stored 2FA state (useful for cleanup)
     */
    clearMFAState(): void {
        runInAction(() => {
            this.mfaToken = undefined
            this.oobCode = undefined
        })
    }

    /**
     * Check if 2FA is in progress
     */
    get isMFAInProgress(): boolean {
        return !!this.mfaToken
    }
}

// Dependency injection decorators
let i = 0
decorate(injectable(), LoginService)
decorate(inject(AuthenticationController), LoginService, i++)
decorate(inject(DeviceTokenService), LoginService, i++)
decorate(inject(SessionService), LoginService, i++)
decorate(inject(new LazyServiceIdentifier(() => CurrentUserPollService)), LoginService, i++)
decorate(inject(new LazyServiceIdentifier(() => CurrentUserController)), LoginService, i++)
decorate(inject(Api), LoginService, i++)

export { LoginService }
export type {
    LoginResult,
    EnterpriseResult,
    PasswordLoginResult,
    MFAResult,
    PasswordResetRequestResult,
    PasswordResetResult
}
